import { BaseballLineupPosition, DraggableList } from '@trackman/baseball-shared-components';
import PlayerType from 'enums/PlayerType';
import { ILineupEntry } from 'interfaces/team/ILineupEntry';
import { observer } from 'mobx-react-lite';
import { LineupManager } from 'models/setLineup/LineupManager';
import React from 'react';
import LineupControls from '../LineupControls/LineupControls';
import { LineupPlayer } from '../LineupPlayer/LineupPlayer';
import { CardWrapper, DragIndicator, LineupListWrapper } from './TeamLineupStyles';

export interface IProps {
  lineupManager: LineupManager;
  unavailablePlayerIds: string[];
  containerClass?: string;
}

export const TeamLineup: React.FC<IProps> = observer((props: IProps) => {
  const { lineupManager, unavailablePlayerIds, containerClass } = props;

  const onDropCompleted = (items: ILineupEntry[]): void => {
    items.forEach((item, index) => {
      item.order = index;
    });
    if (!lineupManager.isPitcherAllowedToBat() && !lineupManager.isScrimmage) {
      const pitcher = lineupManager.lineup.find((item) => item.position === BaseballLineupPosition.Pitcher);
      if (!pitcher) {
        throw Error('Pitcher not found. Something is wrong');
      }
      items.push(pitcher);
    }
    lineupManager.updateLineupOrder(items);
  };

  const renderFixedPitcher = (): JSX.Element | null => {
    if (lineupManager.isBattingPractice || lineupManager.isPitcherAllowedToBat() || lineupManager.isScrimmage) {
      return null;
    }
    const pitcher = lineupManager.lineup.find((item) => item.position === BaseballLineupPosition.Pitcher);
    if (!pitcher) {
      return null;
    }
    return (
      <LineupPlayer
        key='P'
        index='P'
        lineupEntry={pitcher}
        playerSearchService={lineupManager.searchService}
        drag={undefined}
        playerCount={lineupManager.maxPlayers}
        unavailablePlayerIds={lineupManager.getUnavailablePlayerIds(BaseballLineupPosition.Pitcher).concat(unavailablePlayerIds)}
        filledPositions={lineupManager.getFilledPositions()}
        playerType={PlayerType.Pitcher}
        selectablePositions={lineupManager.getSelectablePositions()}
        onChanged={(newEntry?: ILineupEntry): void => lineupManager.updateEntry(pitcher, newEntry)}
        placeholderPlayerConfig={{
          onAddPlaceholderPlayer: lineupManager.addPlayerToRoster,
          allowEditingForeignId: lineupManager.allowForeignIdsOnPlaceholderPlayers,
        }}
        lineup={lineupManager.lineup}
        isScrimmage={lineupManager.isScrimmage}
        containerClass={containerClass}
      />
    );
  };
  const renderItem = (item: ILineupEntry, index: number, isDragging: boolean, dragRef: any): JSX.Element => {
    return (
      <>
        <DragIndicator style={{ display: isDragging ? 'block' : 'none' }} />
        <div style={{ opacity: isDragging ? 0 : 1 }}>
          <LineupPlayer
            key={item.id}
            index={lineupManager.getIndexLabel(index)}
            lineupEntry={item}
            playerCount={lineupManager.lineup.length}
            playerLimit={lineupManager.maxPlayers}
            playerSearchService={lineupManager.searchService}
            drag={dragRef}
            playerType={item.isDesignatedHitter ? PlayerType.DesignatedHitter : PlayerType.Standard}
            unavailablePlayerIds={lineupManager.getUnavailablePlayerIds().concat(unavailablePlayerIds)}
            filledPositions={lineupManager.getFilledPositions()}
            selectablePositions={lineupManager.getSelectablePositions()}
            hidePosition={lineupManager.isBattingPractice}
            onChanged={(newEntry?: ILineupEntry): void => lineupManager.updateEntry(item, newEntry)}
            placeholderPlayerConfig={{
              onAddPlaceholderPlayer: lineupManager.addPlayerToRoster,
              allowEditingForeignId: lineupManager.allowForeignIdsOnPlaceholderPlayers,
            }}
            lineup={lineupManager.lineup}
            isScrimmage={lineupManager.isScrimmage}
            containerClass={containerClass}
          />
        </div>
      </>
    );
  };

  const renderPreview = (item: ILineupEntry, index: number): JSX.Element => {
    return (
      <LineupPlayer
        key={item.order}
        index={lineupManager.getIndexLabel(index)}
        lineupEntry={item}
        playerCount={lineupManager.lineup.length}
        playerSearchService={lineupManager.searchService}
        drag={undefined}
        unavailablePlayerIds={lineupManager.getUnavailablePlayerIds()}
        filledPositions={lineupManager.getFilledPositions()}
        selectablePositions={lineupManager.getSelectablePositions()}
        hidePosition={lineupManager.isBattingPractice}
        onChanged={(newEntry?: ILineupEntry): void => lineupManager.updateEntry(item, newEntry)}
        placeholderPlayerConfig={{
          onAddPlaceholderPlayer: lineupManager.addPlayerToRoster,
          allowEditingForeignId: lineupManager.allowForeignIdsOnPlaceholderPlayers,
        }}
        lineup={lineupManager.lineup}
        isScrimmage={lineupManager.isScrimmage}
        containerClass={containerClass}
      />
    );
  };

  const getDraggableItems = (): ILineupEntry[] => {
    if (!lineupManager.isPitcherAllowedToBat() && !lineupManager.isScrimmage) {
      return lineupManager.lineup.filter((i) => i.position !== BaseballLineupPosition.Pitcher);
    }

    return lineupManager.lineup.slice();
  };

  return (
    <CardWrapper>
      <>
        {!lineupManager.isBattingPractice && <LineupControls lineupManager={lineupManager} />}
        <LineupListWrapper>
          <DraggableList
            items={getDraggableItems()}
            onDropCompleted={onDropCompleted}
            renderItem={renderItem}
            renderPreview={renderPreview}
            id={lineupManager.id}
          />
          {renderFixedPitcher()}
        </LineupListWrapper>
      </>
    </CardWrapper>
  );
});
